/**
 * CompAssignmentModal Component
 * Modal for assigning new greenlight comps with film search and candidate selection
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAssignment } from '../../hooks/useAssignment';
import AssignmentForm from './AssignmentForm';
import { formatErrorMessage } from '../../lib/utils/compFormatters';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../lib/services/apiService';

const CompAssignmentModal = ({ 
  filmId, 
  filmTitle, 
  open, 
  onClose, 
  onSubmit,
  editingComp = null 
}) => {
  const [assignmentData, setAssignmentData] = useState({
    imdbId: '',
    reason: '',
    assignedBy: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use assignment hook and auth context
  const {
    clearError
  } = useAssignment(filmId);
  
  const { userProfile } = useAuth();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setAssignmentData({
        imdbId: editingComp?.imdbId || '',
        reason: editingComp?.reason || '',
        assignedBy: editingComp?.assignedBy || userProfile?.displayName || userProfile?.email || ''
      });
      setError(null);
      clearError();
    }
  }, [open, editingComp, clearError, userProfile]);

  // Handle IMDB ID change
  const handleIMDBChange = (imdbId) => {
    setAssignmentData(prev => ({
      ...prev,
      imdbId
    }));
  };

  // Real film lookup function using API
  const handleFilmLookup = async (imdbId) => {
    try {
      console.log('🔍 Looking up film for IMDB ID:', imdbId);
      
      // Try to get IMDB metadata from the API
      const response = await apiService.getIMDBMetadata(imdbId);
      
      if (response.success && response.data && response.data.movie) {
        const movie = response.data.movie;
        const filmData = {
          filmId: imdbId,
          title: movie.title_text || movie.title || `Film ${imdbId}`,
          releaseYear: movie.release_date?.year || movie.year || 'Unknown',
          genre: movie.genres?.[0] || 'Unknown',
          imdbData: movie
        };
        
        console.log('✅ Film found:', filmData);
        return filmData;
      } else {
        throw new Error('Film not found in database');
      }
    } catch (error) {
      console.error('❌ Film lookup failed:', error.message);
      
      throw new Error(`Film not found: ${error.message}`);
    }
  };

  // Handle assignment form submission
  const handleFormSubmit = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      const assignment = {
        primaryFilmId: filmId,
        compFilmId: formData.imdbId,
        reason: formData.reason,
        assignedBy: formData.assignedBy
      };

      await onSubmit(assignment);
      onClose();
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Render assignment form
  const renderAssignmentForm = () => {
    return (
      <AssignmentForm
        selectedFilm={null}
        assignmentData={assignmentData}
        onSubmit={handleFormSubmit}
        loading={loading}
        error={error}
        editingComp={editingComp}
        onIMDBChange={handleIMDBChange}
        onFilmLookup={handleFilmLookup}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 600
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingComp ? 'Edit Greenlight Comp' : 'Add Greenlight Comp'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {editingComp 
                ? `Editing comparison for ${filmTitle}`
                : `Adding comparison for ${filmTitle}`
              }
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* Assignment Form */}
        <Box sx={{ minHeight: 400 }}>
          {renderAssignmentForm()}
        </Box>

        {/* Global Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CompAssignmentModal;
